import { useTranslation } from '@parsina/framework/clinical/src/i18n/index';
import { MIRROR_ICON } from '@parsina/framework/clinical/src/i18n/index';
import { useState, useEffect, useMemo, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { usePhiSearchTerm } from '../../lib/phiSearch';
import { C } from '@parsina/framework/clinical/src/constants/theme';
import {
  Button, EmptyState, useToast, rangeLabelFor,
} from '@parsina/framework';
import { AccountingMobileLinesSkeleton } from './AccountingSkeleton';
import {
  RotateCw, FileSpreadsheet, Inbox, SearchX, ChevronDown, ChevronLeft, Monitor,
} from 'lucide-react';
import ErrorState from '@parsina/framework/clinical/src/components/common/ErrorState';
import MobileHeader from '@parsina/framework/clinical/src/components/mobile/MobileHeader';
import MobileAccountingSummary from '../../components/mobile/MobileAccountingSummary';
import MobileAccountingFilters from '../../components/mobile/MobileAccountingFilters';
import MobileAccountingLineCard from '../../components/mobile/MobileAccountingLineCard';
import MobileAccountingBreakdown from '../../components/mobile/MobileAccountingBreakdown';

import {
  useAccounting, useMyClinics, fetchAllReportLines,
  presetRange, customRange, translatedPresets,
} from './useAccounting';
import { exportLinesCsv, DEFAULTS } from './Accounting.shared';
import { reportTotals, displayCurrencyFor } from '../../lib/clinicMoney';

// ─────────────────────────────────────────────────────────────────────────────
// Accounting — the mobile report (DESIGN-CONCEPT-MOBILE v2 §1.1, reached by
// tapping the پذیرش income card, WIREFRAME-MOBILE-ACCOUNTING-ACCESS.md).
//
// An independent, app-like surface — NOT the desktop table under a media query.
// It opens with the gradient header (back to پذیرش + «حسابداری» title),
// leads with the money hero (سهم شما), keeps the two most-used filters inline
// with the rest in a BottomSheet, and lists the doctor's own lines as cards that
// grow through «نمایش بیشتر». Behaviour comes from the SAME `useAccounting` hook
// the desktop screen uses — the two surfaces look nothing alike but never
// disagree on a number.
// ─────────────────────────────────────────────────────────────────────────────

const AccountingMobile = () => {
  const { t, formatDate, formatNumber } = useTranslation();
  const toast = useToast();

  // Filter state lives in the URL — the same param names and defaults as the
  // desktop screen (§3.2: the URL is the filter state), so a refresh restores the
  // exact view and the two surfaces can even swap a link. A param at its default
  // is removed; `replace` keeps filter churn out of the history stack. Only the
  // load-more page stays local: the accumulation model can't replay page N alone.
  const [searchParams, setSearchParams] = useSearchParams();
  const param    = (k) => searchParams.get(k) ?? DEFAULTS[k];
  const tab      = param('settle');
  const itemType = param('type');
  const clinicId = param('clinic');
  const preset   = param('preset');
  const custom   = { from: param('from'), to: param('to') };
  // Search stays out of the URL — see Accounting.web.jsx / lib/phiSearch.
  const [query, setQuery] = usePhiSearchTerm('accounting');

  const setParams = (patch) => {
    const { q, ...urlPatch } = patch;
    if (q !== undefined) setQuery(q);
    const next = new URLSearchParams(searchParams);
    for (const [k, v] of Object.entries(urlPatch)) {
      if (v == null || v === '' || v === DEFAULTS[k]) next.delete(k); else next.set(k, v);
    }
    next.delete('q');
    if (next.toString() !== searchParams.toString()) setSearchParams(next, { replace: true });
  };

  const setTab      = (v) => setParams({ settle: v });
  const setItemType = (v) => setParams({ type: v });
  const setClinicId = (v) => setParams({ clinic: v });
  // Preset and custom bounds move together — one URL write, no read-race.
  const onPreset = (id)       => setParams({ preset: id, from: '', to: '' });
  const onCustom = (from, to) => setParams({ preset: 'custom', from, to });

  // Search echoes keystrokes instantly; the URL `q` commits after the debounce.
  const [search, setSearch] = useState(query);
  const dRef = useRef(null);
  const committedRef = useRef(query);   // see Accounting.web.jsx
  useEffect(() => {
    if (query !== committedRef.current) { committedRef.current = query; setSearch(query); }
  }, [query]);
  useEffect(() => () => clearTimeout(dRef.current), []);
  const onSearch = (val) => {
    setSearch(val);
    clearTimeout(dRef.current);
    if (val === '') { committedRef.current = ''; setParams({ q: '' }); return; }
    const commit = val.trim().length < 2 ? '' : val;
    dRef.current = setTimeout(() => { committedRef.current = commit; setParams({ q: commit }); }, 300);
  };

  // Preset/custom → epoch-ms window (same helpers the desktop screen uses).
  const range = useMemo(() => {
    if (preset === 'custom') {
      return (custom.from && custom.to) ? customRange(custom.from, custom.to) : presetRange('month');
    }
    return presetRange(preset);
  }, [preset, custom.from, custom.to]);

  const clinics = useMyClinics();

  // Load-more accumulation for the doctor's own lines: bump `page`, append the
  // fresh rows. Aggregates (summary, breakdowns) always read straight off the
  // whole-window `report`, so they never track this paging.
  const [page, setPage] = useState(1);
  const [acc, setAcc]   = useState([]);
  const filterKey = JSON.stringify({ tab, itemType, clinicId, preset, from: custom.from, to: custom.to, query });
  useEffect(() => { setPage(1); setAcc([]); }, [filterKey]);

  const { report, loading, error, reload, vars } = useAccounting({
    range, clinicId, itemType, settlement: tab, search: query, page, staffPage: 1,
  });

  useEffect(() => {
    if (loading || error) return;
    setAcc((prev) => {
      // A page-1 result replaces (new filter/window); later pages append.
      const base = (report.page ?? 1) <= 1 ? [] : prev;
      const seen = new Set(base.map((l) => l.id));
      const add = report.lines.filter((l) => !seen.has(l.id));
      return (base.length !== prev.length || add.length) ? [...base, ...add] : prev;
    });
  }, [report, loading, error]);

  const rangeLabel = rangeLabelFor(preset, custom.from, custom.to, translatedPresets(t));
  // One summary group per currency, each with its own `outstanding` — never a
  // figure across two currencies. An empty report states its zeros in the
  // selected (or only) clinic's currency.
  const totals = reportTotals(report, displayCurrencyFor(clinics, clinicId));

  const anyRows  = report.linesTotal + report.staffLinesTotal > 0;
  const hasMore  = acc.length < report.linesTotal;
  const hasActiveFilters =
    tab !== DEFAULTS.settle || itemType !== DEFAULTS.type || clinicId !== DEFAULTS.clinic ||
    query !== DEFAULTS.q || preset !== DEFAULTS.preset;
  const activeCount =
    (tab !== DEFAULTS.settle ? 1 : 0) + (itemType !== DEFAULTS.type ? 1 : 0) +
    (clinicId !== DEFAULTS.clinic ? 1 : 0) + (preset !== DEFAULTS.preset ? 1 : 0);

  const clearFilters = () => {
    setSearch('');
    setParams({ settle: '', type: '', clinic: '', q: '', preset: '', from: '', to: '' });
  };

  // State contract (§7): skeleton on the first load only; a refetch dims the
  // previous rows rather than blanking to skeletons.
  const skeleton = loading && acc.length === 0;
  const stale    = loading && acc.length > 0;
  const isEmpty  = !loading && !error && acc.length === 0;
  const dataState = loading && !anyRows ? 'loading' : error ? 'error' : isEmpty ? 'empty' : 'content';

  // Staff-performed lines: on the doctor's invoice, never in a total. The first
  // page shows here; the tail stays desktop-first (§1) rather than silently hidden.
  const [showStaff, setShowStaff] = useState(false);
  const staffShown  = report.staffLines || [];
  const staffHidden = Math.max(0, report.staffLinesTotal - staffShown.length);

  const [exporting, setExporting] = useState(false);
  const onExport = async () => {
    setExporting(true);
    try {
      // Three outcomes, and they are three different sentences (finding 19).
      // An installed iOS PWA has no download UI at all, so the anchor path is a
      // silent no-op there — the doctor pressed export and nothing happened,
      // twice, before giving up. The share sheet is the platform's own answer;
      // where there is neither, saying so is the only honest option.
      const outcome = await exportLinesCsv(await fetchAllReportLines(vars), rangeLabel, t, formatDate);
      if (outcome === 'unsupported') toast.warn(t('accounting.exportDesktopOnly'));
      else if (outcome === 'shared') toast.success(t('accounting.exportShared'));
    } catch (err) {
      console.error('CSV export failed:', err);
      toast.error(t('accounting.exportFailed'));
    } finally {
      setExporting(false);
    }
  };

  // Header quick actions resolve to a destination (§3.2): export appears only
  // when there is something to export.
  const actions = [
    ...(anyRows ? [{ key: 'export', Icon: FileSpreadsheet, label: t('accounting.exportExcel'), onClick: onExport }] : []),
    { key: 'reload', Icon: RotateCw, label: t('errors.reload'), onClick: reload },
  ];

  return (
    <div style={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden', background: C.bg }}>

      {/* Pinned header. حسابداری is a bottom-nav destination (L1) since the
          day's income moved here from the reception, so it has no way back —
          the bar is the way out. */}
      <div style={{ flexShrink: 0 }}>
        <MobileHeader
          title={t('nav.items.accounting')}
          actions={actions}
        />
      </div>

      {/* Scroll region. */}
      <div style={{ flex: 1, minHeight: 0, overflowY: 'auto', WebkitOverflowScrolling: 'touch', paddingBottom: '1.5rem' }}>

        {/* Money hero — sits below the header (no overlap, per user direction).
            Hidden on error: `useAccounting` returns zeros on failure, and a «۰
            تومان» hero would read as real earnings — the false-zero the report
            exists to avoid. The ErrorState below carries the failure instead. */}
        {!error && (
          <div style={{ padding: '0.875rem 0.875rem 0' }}>
            <MobileAccountingSummary totals={totals} loading={skeleton} stale={stale} />
          </div>
        )}

        {/* Filters. */}
        <div style={{ padding: '0.875rem 0.875rem 0' }}>
          <MobileAccountingFilters
            search={search} onSearch={onSearch}
            tab={tab} onTab={setTab}
            preset={preset} custom={custom} onPreset={onPreset}
            onCustom={onCustom}
            presets={translatedPresets(t)} rangeLabel={rangeLabel}
            itemType={itemType} onType={setItemType}
            clinicId={clinicId} onClinic={setClinicId} clinics={clinics}
            activeCount={activeCount} onClear={clearFilters}
          />
        </div>

        {/* Content. */}
        <div style={{ padding: '0.875rem' }} data-testid="accounting-content" data-state={dataState} aria-busy={loading || undefined}>

          {error ? (
            <ErrorState
              error={error}
              title={t('accounting.unavailable')}
              note={t('accounting.unavailableNote')}
              onRetry={reload}
            />
          ) : (
            <>
              {/* List heading. */}
              <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: '0.5rem', marginBottom: '0.625rem' }}>
                <div style={{ fontSize: '0.90625rem', fontWeight: 800, color: C.textPrimary }}>
                  {t('accounting.lineItems')}
                  {report.linesTotal > 0 && (
                    <span style={{ fontSize: '0.75rem', fontWeight: 600, color: C.textMuted, marginInlineStart: '0.5rem' }}>
                      ({formatNumber(report.linesTotal)} {t('common.units.itemsSuffix')}
                    </span>
                  )}
                </div>
                <div style={{ fontSize: '0.6875rem', color: C.textMuted, flexShrink: 0 }}>{rangeLabel}</div>
              </div>

              {skeleton ? (
                <AccountingMobileLinesSkeleton count={4} />
              ) : isEmpty ? (
                hasActiveFilters ? (
                  <EmptyState
                    icon={<SearchX size={48} color={C.textMuted} strokeWidth={1.5} aria-hidden />}
                    title={t('errors.empty.filters')}
                    description={t('accounting.changeRangeOrFilters')}
                    action={
                      <Button variant="outline" size="sm" className="min-h-11" onClick={clearFilters} data-testid="accounting-empty-clear">
                        {t('encounters.clearFilters')}
                      </Button>
                    }
                  />
                ) : (
                  <EmptyState
                    icon={<Inbox size={48} color={C.textMuted} strokeWidth={1.5} aria-hidden />}
                    title={t('accounting.noneInRange')}
                    description={t('accounting.changeRangeHint')}
                  />
                )
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.625rem', transition: 'opacity 0.2s', opacity: stale ? 0.45 : 1, pointerEvents: stale ? 'none' : undefined }}>
                  {acc.map((l) => (
                    <MobileAccountingLineCard key={l.id} line={l} testId={`accounting-row-${l.id}`} />
                  ))}

                  {hasMore && (
                    <Button
                      variant="outline"
                      className="w-full min-h-11"
                      data-testid="accounting-more"
                      onClick={() => setPage((p) => p + 1)}
                    >
                      {t('common.actions.showMorePrefix')}{formatNumber(report.linesTotal - acc.length)} {t('common.units.moreItemsSuffix')}
                    </Button>
                  )}
                </div>
              )}

              {/* Staff-performed lines — outside the doctor's share. */}
              {report.staffLinesTotal > 0 && !skeleton && (
                <div style={{ marginTop: '1rem' }}>
                  <Button
                    variant="ghost"
                    className="w-full min-h-11 justify-start gap-1.5 text-muted-foreground"
                    aria-expanded={showStaff}
                    data-testid="accounting-staff-toggle"
                    onClick={() => setShowStaff((s) => !s)}
                  >
                    {showStaff ? <ChevronDown size={15} aria-hidden /> : <ChevronLeft size={15} aria-hidden style={MIRROR_ICON} />}
                    {formatNumber(report.staffLinesTotal)} {t('accounting.byClinicStaffCount')}
                    <span style={{ fontWeight: 600 }}>{t('accounting.outsideYourShare')}</span>
                  </Button>

                  {showStaff && (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.625rem', marginTop: '0.625rem' }}>
                      {staffShown.map((l) => (
                        <MobileAccountingLineCard key={l.id} line={l} testId={`accounting-staff-row-${l.id}`} />
                      ))}
                      {staffHidden > 0 && (
                        <div style={{
                          display: 'flex', alignItems: 'center', gap: '0.5rem',
                          padding: '0.625rem 0.75rem', borderRadius: '0.75rem',
                          background: C.bg, border: `1px dashed ${C.border}`,
                          fontSize: '0.6875rem', color: C.textMuted,
                        }}>
                          <Monitor size={15} aria-hidden />
                          {formatNumber(staffHidden)} {t('accounting.moreOnDesktop')}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}

              {/* Breakdowns — the desktop rail, re-laid-out for one column. */}
              {!skeleton && !isEmpty && (
                <div style={{ marginTop: '1rem', opacity: stale ? 0.45 : 1, transition: 'opacity 0.2s' }}>
                  <MobileAccountingBreakdown clinics={report.clinics} types={report.types} />
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default AccountingMobile;
